import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";

const SETTINGS_ENDPOINT = "/setting/settings";

const CATEGORY_ORDER = ["Hourly rate", "Intangible benefits", "Others"];

function normaliseCategory(v) {
    const s = String(v ?? "").trim();
    if (!s) return "Others";

    const lower = s.toLowerCase();
    if (lower === "hourly rate" || lower === "hourly") return "Hourly rate";
    if (lower === "intangible benefits (number of hours)" || lower === "intangible benefits" || lower === "intangible") return "Intangible benefits (number of hours)";
    if (lower === "others" || lower === "other") return "Others";

    // future-proof: allow any custom category, but keep it after the known ones
    return s;
}

function categoryRank(cat) {
    const idx = CATEGORY_ORDER.indexOf(cat);
    return idx === -1 ? CATEGORY_ORDER.length : idx;
}

export default function Setting() {
    const navigate = useNavigate();

    const [settings, setSettings] = useState([]);
    const [draft, setDraft] = useState({}); // { [key]: value }
    const [loading, setLoading] = useState(false);
    const [savingKey, setSavingKey] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [query, setQuery] = useState("");

    async function fetchSettings() {
        setErrorMessage("");
        setLoading(true);
        try {
            const res = await axiosInstance.get(SETTINGS_ENDPOINT);
            const list = Array.isArray(res.data) ? res.data : [];

            const normalised = list
                .filter((s) => s && (s.key || s.id))
                .map((s) => ({
                    id: s.id ?? s.key,
                    key: String(s.key ?? ""),
                    value: s.value ?? "",
                    description: s.description ?? s.label ?? "",
                    category: normaliseCategory(s.category),
                    raw: s,
                }))
                .sort((a, b) => a.key.localeCompare(b.key));

            setSettings(normalised);

            const nextDraft = {};
            normalised.forEach((s) => {
                nextDraft[s.key] = s.value ?? "";
            });
            setDraft(nextDraft);
        } catch (err) {
            // console.error("Failed to fetch settings", err);
            setErrorMessage("Failed to load settings. Please try again.");
            toast.error("Failed to load settings.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchSettings();
    }, []);

    const filtered = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        if (!q) return settings;

        return settings.filter((s) => {
            const hay = `${s.key} ${String(draft[s.key] ?? s.value ?? "")} ${s.description} ${s.category}`.toLowerCase();
            return hay.includes(q);
        });
    }, [settings, draft, query]);

    const grouped = useMemo(() => {
        const map = new Map(); // category -> array
        filtered.forEach((s) => {
            const cat = normaliseCategory(s.category);
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push(s);
        });

        // sort each category's items by key
        for (const [cat, arr] of map.entries()) {
            arr.sort((a, b) => a.key.localeCompare(b.key));
            map.set(cat, arr);
        }

        // order categories: Hourly rate, Intangible benefits, Others, then any custom categories alphabetically
        const cats = Array.from(map.keys());
        const known = cats
            .filter((c) => CATEGORY_ORDER.includes(c))
            .sort((a, b) => categoryRank(a) - categoryRank(b));

        const unknown = cats
            .filter((c) => !CATEGORY_ORDER.includes(c))
            .sort((a, b) => a.localeCompare(b));

        const orderedCats = [...known, ...unknown];

        return orderedCats.map((cat) => ({
            category: cat,
            items: map.get(cat) ?? [],
        }));
    }, [filtered]);

    function originalValueForKey(key) {
        const found = settings.find((s) => s.key === key);
        return found?.value ?? "";
    }

    function isDirty(key) {
        return String(draft[key] ?? "") !== String(originalValueForKey(key) ?? "");
    }

    function guessInputKind(value) {
        const v = value ?? "";
        const s = String(v).trim();

        if (s === "true" || s === "false") return "boolean";
        if (s.length > 120) return "textarea";

        if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
            return "textarea";
        }

        if (s !== "" && !Number.isNaN(Number(s)) && /^[+-]?\d+(\.\d+)?$/.test(s)) return "number";

        return "text";
    }

    function prettifyJSON(key) {
        try {
            const raw = String(draft[key] ?? "");
            const parsed = JSON.parse(raw);
            setDraft((prev) => ({ ...prev, [key]: JSON.stringify(parsed, null, 2) }));
            toast.success("JSON formatted.");
        } catch {
            toast.error("Invalid JSON. Cannot format.");
        }
    }

    async function saveSetting(key) {
        setErrorMessage("");
        if (!key) return;

        const value = draft[key];
        setSavingKey(key);

        try {
            const res = await axiosInstance.put(`${SETTINGS_ENDPOINT}/${encodeURIComponent(key)}`, {
                value,
            });

            const updated = res?.data;

            setSettings((prev) =>
                prev.map((s) =>
                    s.key === key
                        ? {
                            ...s,
                            value: updated?.value ?? value,
                            description: updated?.description ?? s.description,
                            category: normaliseCategory(updated?.category ?? s.category),
                            raw: updated ?? s.raw,
                        }
                        : s
                )
            );

            setDraft((prev) => ({ ...prev, [key]: updated?.value ?? value }));
            toast.success(`Saved: ${key}`);
        } catch (err) {
            console.error("Failed to update setting", err);
            setErrorMessage("Failed to save setting. Please try again.");
            toast.error("Failed to save setting.");
        } finally {
            setSavingKey(null);
        }
    }

    function resetRow(key) {
        setDraft((prev) => ({ ...prev, [key]: originalValueForKey(key) }));
        toast.info("Reverted changes.");
    }

    const isSaving = Boolean(savingKey);

    function renderSettingCard(s) {
        const key = s.key;
        const value = draft[key] ?? "";
        const kind = guessInputKind(value);
        const dirty = isDirty(key);
        const rowSaving = savingKey === key;

        const valueStr = String(value ?? "").trim();
        const looksJson = valueStr.startsWith("{") || valueStr.startsWith("[");

        return (
            <div key={s.id} className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="font-semibold text-slate-900">{key}</div>
                            {dirty && (
                                <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800 ring-1 ring-amber-100">
                                    Unsaved changes
                                </span>
                            )}
                        </div>
                        {s.description ? <p className="mt-1 text-sm text-slate-500">{s.description}</p> : null}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        {kind === "textarea" && (
                            <OutlineButton
                                onClick={() => prettifyJSON(key)}
                                disabled={rowSaving || !looksJson}
                                title="Format JSON (only if value is valid JSON)"
                            >
                                Format JSON
                            </OutlineButton>
                        )}

                        <OutlineButton onClick={() => resetRow(key)} disabled={rowSaving || !dirty}>
                            Revert
                        </OutlineButton>

                        <PrimaryButton onClick={() => saveSetting(key)} disabled={rowSaving || !dirty}>
                            {rowSaving ? "Saving..." : "Save"}
                        </PrimaryButton>
                    </div>
                </div>

                <div className="mt-4 flex-1">
                    {kind === "boolean" ? (
                        <select
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                            value={String(value)}
                            onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                            disabled={rowSaving}
                        >
                            <option value="true">true</option>
                            <option value="false">false</option>
                        </select>
                    ) : kind === "number" ? (
                        <input
                            type="number"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                            value={value}
                            onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                            disabled={rowSaving}
                        />
                    ) : kind === "textarea" ? (
                        <textarea
                            className="min-h-[120px] w-full resize-vertical rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                            value={value}
                            onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                            disabled={rowSaving}
                        />
                    ) : (
                        <input
                            type="text"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                            value={value}
                            onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                            disabled={rowSaving}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 py-6 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-6xl rounded-3xl bg-white/95 p-8 shadow-2xl ring-1 ring-slate-200">
                {/* Header */}
                <header className="mb-6 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
                        <h1 className="text-2xl font-semibold text-slate-900">Setting</h1>

                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-[11px] font-medium text-indigo-700 ring-1 ring-indigo-100 sm:text-xs">
                                Admin configuration
                            </span>
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-700 sm:text-xs">
                                {settings.length} setting{settings.length === 1 ? "" : "s"}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <div className="relative">
                            <input
                                type="text"
                                className="w-64 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                placeholder="Search settings..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <OutlineButton onClick={fetchSettings} disabled={loading || isSaving}>
                            {loading ? "Refreshing..." : "Refresh"}
                        </OutlineButton>
                    </div>
                </header>

                {/* Error banner */}
                {errorMessage && (
                    <div className="mb-4 rounded-md border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-800">
                        {errorMessage}
                    </div>
                )}

                {/* Content */}
                {loading ? (
                    <div className="mt-4 space-y-3">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                <div className="h-4 w-48 rounded bg-slate-200" />
                                <div className="mt-3 h-10 w-full rounded bg-slate-200" />
                            </div>
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
                        No settings match your search.
                    </div>
                ) : (
                    <div className="mt-4 space-y-8">
                        {grouped.map((section) => (
                            <section key={section.category}>
                                <div className="mb-3 flex items-center justify-between">
                                    <h2 className="text-sm font-semibold text-slate-900">
                                        {section.category}
                                    </h2>
                                    <span className="text-xs text-slate-500">
                                        {section.items.length} item{section.items.length === 1 ? "" : "s"}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                                    {section.items.map(renderSettingCard)}
                                </div>
                            </section>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// --------------------------------------------------
// Shared UI elements (matches CreateTicket style)
// --------------------------------------------------
function PrimaryButton(props) {
    const { className, ...rest } = props;
    return (
        <button
            {...rest}
            className={
                "inline-flex items-center rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60 " +
                (className || "")
            }
        />
    );
}

function OutlineButton(props) {
    const { className, ...rest } = props;
    return (
        <button
            {...rest}
            className={
                "inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-900 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 " +
                (className || "")
            }
        />
    );
}
